import {
  type ButtonHTMLAttributes,
  type FormHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useState,
} from "react";

/**
 * The portal's few building blocks, so the pages look alike and their class lists stay short. Only
 * presentation: each renders the element it stands for (a `<button>`, a `<label>`, a `<table>`)
 * and passes everything else through.
 */

/** Class names, the falsy ones left out. */
export const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");

/** A page: its title, and a link back where there is one. */
export function PageLayout({
  title,
  back,
  children,
}: {
  title: ReactNode;
  back?: { href: string; label: string };
  children: ReactNode;
}) {
  return (
    <section>
      {back && (
        <p className="mt-0 mb-4 text-ui">
          <a href={back.href} className="text-muted hover:text-fg">
            {back.label}
          </a>
        </p>
      )}
      <h1 className="mt-0">{title}</h1>
      {children}
    </section>
  );
}

/** An id in a page's title: quieter than the words before it. */
export function TitleId({ children }: { children: ReactNode }) {
  return (
    <span className="font-medium text-[0.68em] text-fg-2 tracking-normal [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </span>
  );
}

/** A page's toolbar: its filters, their button and the note on what they mean. */
export function Toolbar(props: FormHTMLAttributes<HTMLFormElement>) {
  return (
    <form
      {...props}
      className={cx("my-4 flex flex-wrap items-center gap-x-3 gap-y-2", props.className)}
    />
  );
}

/** A labelled control: the label's text, then the control inside the label. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="inline-flex items-center gap-2 font-medium text-fg-2 text-ui">
      {label} {children}
    </label>
  );
}

const control =
  "h-[30px] rounded-md border border-control bg-raised px-2 text-ui text-fg placeholder:text-muted focus:border-focus focus:ring-3 focus:ring-focus-ring focus:outline-none";

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(control, props.className)} />;
}

export function Select({
  compact,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { compact?: boolean }) {
  return <select {...props} className={cx(control, compact && "h-[26px]", props.className)} />;
}

const BUTTONS = {
  secondary:
    "border-line-strong bg-raised text-fg shadow-control enabled:hover:bg-hover enabled:active:bg-active",
  primary: "border-transparent bg-accent-solid text-on-accent enabled:hover:bg-accent-solid-hover",
  quiet: "border-transparent text-muted enabled:hover:bg-hover enabled:hover:text-fg",
};

const SIZES = {
  md: "h-[30px] px-3 text-ui",
  sm: "h-[26px] px-2.5 text-xs",
  lg: "h-9 px-4 text-sm",
};

/** A button: secondary unless it is the page's one main action. */
export function Button({
  variant = "secondary",
  size = "md",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof BUTTONS;
  size?: keyof typeof SIZES;
}) {
  return (
    <button
      {...props}
      className={cx(
        "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border font-medium transition-colors duration-75 disabled:cursor-not-allowed disabled:opacity-45",
        BUTTONS[variant],
        SIZES[size],
        props.className,
      )}
    />
  );
}

/** A button that reads as a link, for an action inside text or a table cell. */
export function LinkButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cx(
        "cursor-pointer text-accent underline-offset-2 hover:underline aria-pressed:font-semibold",
        props.className,
      )}
    />
  );
}

const TONES = {
  neutral: "bg-tag text-fg-2",
  ok: "bg-ok-tint text-ok",
  bad: "bg-bad-tint text-bad",
};

/** A short status: a state, yes or no. With a dot when it is a live state. */
export function Badge({
  tone = "neutral",
  dot,
  children,
}: {
  tone?: keyof typeof TONES;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-[5px] whitespace-nowrap rounded-full px-[7px] align-[1px] font-medium text-xs/5",
        TONES[tone],
        dot && "before:size-1.5 before:rounded-full before:bg-current",
      )}
    >
      {children}
    </span>
  );
}

const NOTICES = {
  ok: "bg-ok-tint text-ok",
  bad: "bg-bad-tint text-bad",
  info: "bg-tag text-fg-2 text-ui",
};

/** A notice that stands as its own paragraph: a done change, a warning, an error or a note. */
export function Notice({
  tone,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { tone: keyof typeof NOTICES }) {
  return (
    <p {...props} className={cx("my-3 rounded-md px-3 py-2", NOTICES[tone], props.className)} />
  );
}

/**
 * A table in a scroller of its own, so a wide one scrolls sideways rather than the page. A compact
 * one, of a few columns, is as wide as its content needs.
 */
export function Table({ compact, children }: { compact?: boolean; children: ReactNode }) {
  return (
    <div className="scroll-shadows my-2 mb-4 overflow-x-auto">
      <table className={compact ? "w-auto min-w-[min(100%,36rem)]" : undefined}>{children}</table>
    </div>
  );
}

/**
 * A button for a change that cannot be undone (deleting a login, revoking a device): the first click
 * asks, in place, and only the confirming button acts.
 */
export function ConfirmButton({
  label,
  question,
  confirm,
  disabled,
  onConfirm,
}: {
  label: string;
  question: string;
  confirm: string;
  disabled?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <Button type="button" size="sm" disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }
  return (
    <span role="group" aria-label={question} className="inline-flex items-center gap-1.5">
      <span className="text-ui">{question}</span>
      <Button
        type="button"
        size="sm"
        variant="primary"
        onClick={() => {
          setAsking(false);
          void onConfirm();
        }}
      >
        {confirm}
      </Button>
      <Button type="button" size="sm" variant="quiet" onClick={() => setAsking(false)}>
        Cancel
      </Button>
    </span>
  );
}

/** Facts as a property list: names on the left, values on the right. */
export const facts =
  "my-3 mb-6 grid grid-cols-[minmax(6.5rem,max-content)_minmax(0,1fr)] gap-x-4 gap-y-1.5 md:grid-cols-[minmax(8rem,max-content)_minmax(0,1fr)] md:gap-x-8 [&_dd]:m-0 [&_dd]:min-w-0 [&_dd]:wrap-anywhere [&_dt]:text-muted";
